<template>
  <Modal
    data-cy="acceptPaymentModal"
    :title="`Accept Payment, Invoice #${invoice?.invoice_id}`"
    :saving="saving"
    class-name="acceptPaymentModal"
    saving-text="Processing..."
    submit-btn-text="Pay"
    @hide-modal="$emit('hide-modal')"
    @submit="handleApply"
  >
    <div v-if="billingCustomer" class="accept-payment">
      <div class="flex flex-col divide-y gap-4">
        <div>
          <div class="text-title">Bill To</div>
          <div class="section-content d-flex flex-column customer-info">
            <h3 title="customer name" data-cy="customerFullName" class="mb-0">
              {{ billingCustomer.full_name }}
            </h3>
            <div class="flex flex-col md:flex-row items-start">
              <span class="whitespace-nowrap">{{ billingCustomer.phone }}</span>
              <span data-cy="billingAddress">{{ billingAddress }}</span>
            </div>
          </div>
        </div>
        <div class="pt-4">
          <div class="text-title">Payment Info</div>
          <div class="section-content">
            <div class="d-flex payment-values mt-2 mb-2 gap-1">
              <div class="d-flex">
                <span class="mr-2 nowrap">Invoice Total:</span>
                <span data-cy="invoiceTotal">{{
                  dollars(invoice.financials.total_cents)
                }}</span>
              </div>
              <div class="d-flex">
                <span class="mr-2 nowrap">Balance Remaining:</span>
                <span
                  data-cy="balanceRemaining"
                  :class="[
                    'mr-1',
                    {
                      'text-danger': invoice.financials.balance_cents > 0
                    }
                  ]"
                  >{{ dollars(invoice.financials.balance_cents) }}</span
                >
              </div>
              <div class="d-flex justify-content-end payment-amount-label">
                <span class="mr-2 nowrap">Payment Amount:</span>
                <TextField
                  v-model="paymentAmount"
                  name="paymentAmount"
                  data-cy="paymentAmount"
                  currency
                  style="width: 130px"
                  class="mb-0 payment-amount-field"
                  input-class="text-right"
                  label="Payment Amount"
                  no-label
                />
              </div>
            </div>
            <div
              v-if="v$.paymentAmount.$errors.length"
              class="validation-payment-message d-flex"
            >
              <div class="d-flex align-items-center ml-auto">
                <span
                  class="icon fa-regular fa-triangle-exclamation mr-2"
                ></span>
                <span>{{ v$.paymentAmount.$errors[0]?.$message }}</span>
              </div>
            </div>
            <hr />
            <PaymentMethods
              ref="paymentForm"
              class="pt-4"
              data-cy="paymentMethods"
              :invoice-id="invoice.invoice_id"
              :job-id="invoice.job_id"
              :customer="billingCustomer"
              :payment-amount-cents="paymentAmount"
              :financing-enabled="financingEnabled"
            />
          </div>
        </div>
      </div>
    </div>
    <template v-if="disabled" #submit>
      <div></div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import { Customer } from "@/typings/customer";
import { Invoice } from "@/typings/invoice";
import { useVuelidate } from "@vuelidate/core";
import { helpers, maxValue, minValue, required } from "@vuelidate/validators";
import Modal from "../components/Modal.vue";
import TextField from "../components/TextField.vue";
import PaymentMethods from "./PaymentMethods.vue";
import usePaymentMethods from "./usePaymentMethods";

export default {
  name: "AcceptPayment",
  components: {
    Modal,
    PaymentMethods,
    TextField
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object as () => Invoice,
      default: null
    }
  },
  emits: ["payment-success", "payment-method-upated", "hide-modal"],
  setup() {
    const { paymentAmountErrorMessage, fetchPaymentReceivers } =
      usePaymentMethods();
    return {
      paymentAmountErrorMessage,
      fetchPaymentReceivers,
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      paymentAmount: {
        required,
        minValue: minValue(0.01),
        maxValue: helpers.withMessage(
          "Cannot charge more than the balance due",
          maxValue(this.invoice?.financials.balance_cents)
        )
      }
    };
  },
  data() {
    return {
      hasPaymentAmountError: false,
      paymentAmount: 0,
      saving: false
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant", "financingEnabled"]),
    billingAddress({ invoice }) {
      if (!invoice.billing_address) {
        return "";
      }
      const { street, city, state, zip_code } = invoice.billing_address;
      return `${street} ${city}, ${state} ${zip_code}`;
    },
    billingCustomer({ invoice }): Customer {
      return invoice?.billing_customer;
    }
  },
  mounted() {
    // Pre-populate payment amount based on outstanding balance
    if (this.invoice) {
      this.paymentAmount = this.invoice.financials.balance_cents;
      this.fetchPaymentReceivers(this.invoice.payment_receivers_url);
      this.fetchTransactions(this.invoice);
    }
  },
  methods: {
    ...mapActions(["fetchTransactions", "createTransaction"]),
    async handleApply() {
      if (!this.saving) {
        window.notify.close();
        this.saving = true;
        const valid = await this.v$.$validate();

        if (!valid) {
          this.saving = false;
          return;
        }

        try {
          const formData = await this.$refs.paymentForm.getData();

          const minAmount = 1;
          if (this.paymentAmount < minAmount) {
            window.notify.error(
              `Minimum payment amount of $${minAmount / 100} not met!`
            );
            return;
          }

          const data = {
            amount_cents: this.paymentAmount,
            ...formData
          };

          await this.createTransaction({
            invoice: this.invoice,
            data
          });

          window.notify.success("Payment processed successfully!");
          this.$emit("payment-success");
          this.$emit("hide-modal");

          // Use the bus since this needs to be handled in grandparent component(s)
          if (
            formData.card_save_payment_method ||
            formData.ach_save_payment_method
          ) {
            EventBus.$emit("payment-method-updated");
          }
        } catch (err) {
          const error = err; // make debuggable
          console.log(
            "Error occurred on payment (should have already notified internally): "
          );
          console.log(error.message || error);
        } finally {
          this.saving = false;
        }
      }
    },
    async validate() {
      return await this.v$.$validate();
    }
  }
};
</script>

<style lang="scss">
.acceptPaymentModal {
  .validation-payment-message {
    margin-left: auto;
    color: var(--danger);
    .icon {
      font-size: 24px;
    }
  }
  .modal-footer {
    padding-top: 1em;
  }
  .modal-dialog {
    max-width: 800px !important;
  }

  .payment-values {
    font-weight: bold;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .payment-amount-field {
    width: 120px !important;
    margin-top: -5px;
  }

  .nowrap {
    white-space: nowrap;
  }

  @media (max-width: 800px) {
    .modal-dialog {
      margin-right: 10px;
      margin-left: 10px;
    }
  }

  @media (max-width: 705px) {
    .payment-values {
      flex-direction: column !important;
    }
    .payment-amount-label {
      justify-content: start !important;
    }
    .payment-amount-field {
      margin-top: 0px;
    }
  }
}
</style>
